/**
 * Custom Select Dropdown for Variant Selectors
 * Replaces native select dropdowns with custom styled dropdowns
 */

(function() {
  'use strict';

  // Check if already initialized
  if (window.customSelectInitialized) {
    return;
  }
  window.customSelectInitialized = true;
  function getVariantDataForOption(selectElement, optionElement) {
    try {
      const variantIdFromOption = optionElement.getAttribute('data-variant-id');
      if (variantIdFromOption) {
        const priceAttr = optionElement.getAttribute('data-variant-price');
        const compareAttr = optionElement.getAttribute('data-variant-compare-at-price');
        const availableAttr = optionElement.getAttribute('data-variant-available');
        const parsedPrice = priceAttr != null ? parseInt(priceAttr, 10) : null;
        const parsedCompare = compareAttr != null ? parseInt(compareAttr, 10) : null;
        const isAvailable = availableAttr == null ? true : availableAttr === 'true';
        return {
          price: Number.isFinite(parsedPrice) ? parsedPrice : 0,
          compare_at_price: Number.isFinite(parsedCompare) ? parsedCompare : parsedPrice,
          available: isAvailable,
        };
      }

      const variantSelectsContainer = selectElement.closest('variant-selects, [data-custom-select-scope]');
      if (!variantSelectsContainer) return null;

      const variantsScript = variantSelectsContainer.querySelector('[data-product-variants]');
      if (!variantsScript) return null;

      const variants = JSON.parse(variantsScript.textContent);
      if (!variants || !Array.isArray(variants)) return null;

      const optionWrapper = selectElement.closest('[data-option-index]');
      let optionPosition = 1;

      if (optionWrapper) {
        const optionIndex = optionWrapper.getAttribute('data-option-index');
        if (optionIndex) {
          const match = optionIndex.match(/option(\d+)/);
          if (match) {
            optionPosition = parseInt(match[1]);
          }
        }
      }

      const optionValue = optionElement.getAttribute('data-option-value') || optionElement.value;

      // Always build selectedValues from live DOM instead of trusting stale data-selected-variant
      const selectedValues = {};

      // Query all possible input types for variant options
      const allSelects = variantSelectsContainer.querySelectorAll('select.select');
      const allRadios = variantSelectsContainer.querySelectorAll('input[type="radio"]:checked');
      const allHiddenInputs = variantSelectsContainer.querySelectorAll('input[type="hidden"][data-option-value]');

      // Collect from select elements
      if (allSelects && allSelects.length > 0) {
        allSelects.forEach((sel, idx) => {
          const selected = sel.options[sel.selectedIndex];
          if (selected && selected.value) {
            selectedValues[idx + 1] = selected.value;
          }
        });
      }

      // Collect from checked radio buttons (may override select values)
      if (allRadios && allRadios.length > 0) {
        allRadios.forEach((radio) => {
          const radioWrapper = radio.closest('[data-option-index]');
          if (radioWrapper) {
            const optionIndex = radioWrapper.getAttribute('data-option-index');
            if (optionIndex) {
              const match = optionIndex.match(/option(\d+)/);
              if (match) {
                const pos = parseInt(match[1]);
                selectedValues[pos] = radio.value || radio.getAttribute('data-option-value');
              }
            }
          }
        });
      }

      // Collect from hidden inputs with data-option-value
      if (allHiddenInputs && allHiddenInputs.length > 0) {
        allHiddenInputs.forEach((input) => {
          const inputWrapper = input.closest('[data-option-index]');
          if (inputWrapper) {
            const optionIndex = inputWrapper.getAttribute('data-option-index');
            if (optionIndex) {
              const match = optionIndex.match(/option(\d+)/);
              if (match) {
                const pos = parseInt(match[1]);
                const value = input.value || input.getAttribute('data-option-value');
                if (value) {
                  selectedValues[pos] = value;
                }
              }
            }
          }
        });
      }

      for (const variant of variants) {
        const variantOptionValue = variant['option' + optionPosition];
        if (variantOptionValue !== optionValue) continue;

        let matchesOthers = true;
        for (let i = 1; i <= 3; i++) {
          if (i === optionPosition) continue;
          const selectedValue = selectedValues[i];
          const variantValue = variant['option' + i];
          if (selectedValue && variantValue && selectedValue !== variantValue) {
            matchesOthers = false;
            break;
          }
        }

        if (matchesOthers) {
          return variant;
        }
      }

      for (const variant of variants) {
        if (variant['option' + optionPosition] === optionValue) {
          return variant;
        }
      }

      return null;
    } catch (error) {
      console.warn('Error getting variant data:', error);
      return null;
    }
  }
  function formatPrice(priceInCents, selectElement) {
    try {
      const isBundleQtySelector = !!selectElement?.closest('bundle-qty-selector');
      if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
        const moneyFormat = (theme.settings && theme.settings.moneyFormat) || '';
        const formattedPrice = theme.Currency.formatMoney(priceInCents, moneyFormat);
        return isBundleQtySelector
          ? formattedPrice.replace(/,(?=\d{3}\b)/g, ' ')
          : formattedPrice;
      }
    } catch (error) {
      console.warn('Error using theme.Currency.formatMoney:', error);
    }

    try {
      if (typeof window.themeVariables !== 'undefined' &&
          window.themeVariables.settings &&
          window.themeVariables.settings.moneyFormat) {
        const moneyFormat = window.themeVariables.settings.moneyFormat;
        const price = (parseInt(priceInCents) / 100).toFixed(2);
        return moneyFormat.replace('{{amount}}', price)
                          .replace('{{amount_with_comma_separator}}', price.replace('.', ','))
                          .replace('{{amount_no_decimals}}', Math.round(parseInt(priceInCents) / 100));
      }
    } catch (error) {
      console.warn('Error using manual money formatting:', error);
    }

    const price = (parseInt(priceInCents) / 100).toFixed(2);
    return price.replace('.', ',') + ' Kč';
  }

  class CustomSelect {
    constructor(selectElement) {
      this.selectElement = selectElement;
      this.wrapper = null;
      this.trigger = null;
      this.optionsContainer = null;
      this.isOpen = false;

      this.selectElement._customSelectInstance = this;

      this.init();
    }

    init() {
      if (this.selectElement.classList.contains('custom-select-hidden') && this.wrapper) {
        return;
      }

      this.createCustomDropdown();
      this.selectElement.classList.add('custom-select-hidden');
      this.setupEventListeners();
      this.observeSelectElement();
    }

    observeSelectElement() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (!this.selectElement.classList.contains('custom-select-hidden')) {
              this.selectElement.classList.add('custom-select-hidden');
            }
          }
        });
      });

      observer.observe(this.selectElement, {
        attributes: true,
        attributeFilter: ['class']
      });

      this.classObserver = observer;
    }

    createCustomDropdown() {
      this.wrapper = document.createElement('div');
      this.wrapper.className = 'custom-select-wrapper';

      this.trigger = document.createElement('div');
      this.trigger.className = 'custom-select-trigger';

      this.trigger.textContent = this.getSelectedOptionText();

      this.trigger.setAttribute('role', 'button');
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');

      this.optionsContainer = document.createElement('div');
      this.optionsContainer.className = 'custom-select-options';
      this.optionsContainer.setAttribute('role', 'listbox');

      this.createOptions();

      this.wrapper.appendChild(this.trigger);
      this.wrapper.appendChild(this.optionsContainer);

      this.selectElement.parentNode.insertBefore(this.wrapper, this.selectElement.nextSibling);
    }

    createOptions() {
      const options = Array.from(this.selectElement.options);

      options.forEach((option, index) => {
        const optionDiv = document.createElement('div');
        optionDiv.className = 'custom-select-option';
        optionDiv.setAttribute('data-value', option.value);
        optionDiv.setAttribute('data-index', index);
        optionDiv.setAttribute('role', 'option');

        let variantData = getVariantDataForOption(this.selectElement, option);
        if (!variantData) {
          const fallbackPrice = parseInt(option.getAttribute('data-variant-price') || '', 10);
          const fallbackCompare = parseInt(option.getAttribute('data-variant-compare-at-price') || '', 10);
          if (Number.isFinite(fallbackPrice)) {
            const fallbackAvailableAttr = option.getAttribute('data-variant-available');
            const fallbackIsAvailable = fallbackAvailableAttr == null ? true : fallbackAvailableAttr === 'true';
            variantData = {
              price: fallbackPrice,
              compare_at_price: Number.isFinite(fallbackCompare) ? fallbackCompare : fallbackPrice,
              available: fallbackIsAvailable,
            };
          }
        }

        const variantSelectsContainer = this.selectElement.closest('variant-selects');
        const templateSuffix = variantSelectsContainer ? variantSelectsContainer.getAttribute('data-template-suffix') : '';
        const isSetPovleceni = templateSuffix === 'set-povleceni';
        const isGiftCard = templateSuffix === 'gift-card';

        if (variantData && variantData.price != null && !isSetPovleceni && !isGiftCard) {
          const contentWrapper = document.createElement('div');
          contentWrapper.className = 'custom-select-option-content';

          const label = document.createElement('span');
          label.className = 'custom-select-option-label';
          label.textContent = option.textContent.trim();
          contentWrapper.appendChild(label);

          const pricesContainer = document.createElement('span');
          pricesContainer.className = 'custom-select-option-prices';

          const isAvailable = variantData.available !== false;
          const soldOutText =
            this.selectElement.getAttribute('data-sold-out-label') ||
            window.themeVariables?.strings?.soldOut ||
            'Sold out';

          if (isAvailable) {
            const numericPrice = typeof variantData.price === 'number' ? variantData.price : parseFloat(variantData.price) || 0;
            const numericComparePrice = variantData.compare_at_price != null ? (typeof variantData.compare_at_price === 'number' ? variantData.compare_at_price : parseFloat(variantData.compare_at_price) || 0) : null;

            if (numericComparePrice != null && numericComparePrice > numericPrice) {
              const comparePrice = document.createElement('span');
              comparePrice.className = 'custom-select-option-compare-price';
              comparePrice.textContent = formatPrice(variantData.compare_at_price, this.selectElement);
              pricesContainer.appendChild(comparePrice);
            }

            const price = document.createElement('span');
            price.className = 'custom-select-option-price';
            price.textContent = formatPrice(variantData.price, this.selectElement);
            pricesContainer.appendChild(price);
          } else {
            const soldOut = document.createElement('span');
            soldOut.className = 'custom-select-option-price';
            soldOut.textContent = soldOutText;
            pricesContainer.appendChild(soldOut);
          }

          contentWrapper.appendChild(pricesContainer);
          optionDiv.appendChild(contentWrapper);
        } else {
          optionDiv.textContent = option.textContent.trim();
        }

        if (option.selected) {
          optionDiv.classList.add('selected');
          optionDiv.setAttribute('aria-selected', 'true');
        }

        if (option.disabled) {
          optionDiv.classList.add('disabled');
          optionDiv.setAttribute('aria-disabled', 'true');
        }

        this.optionsContainer.appendChild(optionDiv);
      });
    }

    getSelectedOptionText() {
      const selectedOption = this.selectElement.options[this.selectElement.selectedIndex];
      return selectedOption ? selectedOption.textContent.trim() : '';
    }

    setupEventListeners() {
      this.trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleDropdown();
      });

      this.optionsContainer.addEventListener('click', (e) => {
        const optionEl = e.target.closest('.custom-select-option');
        if (optionEl && !optionEl.classList.contains('disabled')) {
          this.selectOption(optionEl);
        }
      });

      this._onDocumentClick = (e) => {
        if (!this.wrapper.contains(e.target)) {
          this.closeDropdown();
        }
      };
      document.addEventListener('click', this._onDocumentClick);

      this.trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleDropdown();
        } else if (e.key === 'Escape') {
          this.closeDropdown();
        }
      });

      this.selectElement.addEventListener('change', () => {
        // Rebuild options to recompute prices based on new variant selection
        this.optionsContainer.innerHTML = '';
        this.createOptions();
        this.updateCustomDropdown();
      });
    }

    destroy() {
      if (this._onDocumentClick) {
        document.removeEventListener('click', this._onDocumentClick);
      }
      if (this.classObserver) {
        this.classObserver.disconnect();
      }
      if (this.wrapper && this.wrapper.parentNode) {
        this.wrapper.remove();
      }
      if (this.selectElement._customSelectInstance === this) {
        delete this.selectElement._customSelectInstance;
      }
      this.selectElement.classList.remove('custom-select-hidden');
    }

    toggleDropdown() {
      if (this.isOpen) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    }

    openDropdown() {
      this.isOpen = true;
      this.wrapper.classList.add('open');
      this.trigger.setAttribute('aria-expanded', 'true');

      const selectedOption = this.optionsContainer.querySelector('.custom-select-option.selected');
      if (selectedOption) {
        selectedOption.scrollIntoView({ block: 'nearest' });
      }
    }

    closeDropdown() {
      this.isOpen = false;
      this.wrapper.classList.remove('open');
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    selectOption(optionDiv) {
      const index = parseInt(optionDiv.getAttribute('data-index'));

      this.selectElement.selectedIndex = index;

      const changeEvent = new Event('change', { bubbles: true });
      this.selectElement.dispatchEvent(changeEvent);

      this.updateCustomDropdown();

      this.closeDropdown();
    }

    updateCustomDropdown() {
      this.trigger.textContent = this.getSelectedOptionText();

      const options = this.optionsContainer.querySelectorAll('.custom-select-option');
      options.forEach((option, index) => {
        if (index === this.selectElement.selectedIndex) {
          option.classList.add('selected');
          option.setAttribute('aria-selected', 'true');
        } else {
          option.classList.remove('selected');
          option.setAttribute('aria-selected', 'false');
        }
      });
    }

    rebuildOptions() {
      this.optionsContainer.innerHTML = '';
      this.createOptions();
    }
  }
  function initCustomSelects() {
    try {
      const variantSelects = document.querySelectorAll('variant-selects .select[is="custom-select"], [data-custom-select-scope] .select[is="custom-select"]');

      variantSelects.forEach(select => {
        if (select._customSelectInstance) {
          if (!select.classList.contains('custom-select-hidden')) {
            select.classList.add('custom-select-hidden');
          }
          return;
        }

        if (!select.classList.contains('custom-select-hidden')) {
          new CustomSelect(select);
        }
      });
    } catch (error) {
      console.error('Error initializing custom selects:', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomSelects);
  } else {
    initCustomSelects();
  }

  document.addEventListener('shopify:section:load', initCustomSelects);

  document.addEventListener('quickview:loaded', function() {
    setTimeout(initCustomSelects, 100);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupVariantListeners);
  } else {
    setupVariantListeners();
  }

  function setupVariantListeners() {
    if (typeof theme !== 'undefined' && theme.pubsub) {
      theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
        function() {
          setTimeout(initCustomSelects, 50);
        }
      );
    }

    document.addEventListener('change', function(e) {
      if (e.target.matches('variant-selects .select[is="custom-select"], [data-custom-select-scope] .select[is="custom-select"]')) {
        setTimeout(initCustomSelects, 50);
      }
    });
  }

  const variantObserver = new MutationObserver(function(mutations) {
    let shouldReinit = false;

    mutations.forEach(function(mutation) {
      if (mutation.type === 'childList' || mutation.type === 'attributes') {
        const selects = document.querySelectorAll('variant-selects .select[is="custom-select"]');
        selects.forEach(function(select) {
          if (select._customSelectInstance && !select.classList.contains('custom-select-hidden')) {
            shouldReinit = true;
          }
        });
      }
    });

    if (shouldReinit) {
      initCustomSelects();
    }
  });

  function observeVariantContainers() {
    const containers = document.querySelectorAll('variant-selects, [data-custom-select-scope]');
    containers.forEach(function(container) {
      if (!container.hasAttribute('data-custom-select-observed')) {
        container.setAttribute('data-custom-select-observed', 'true');
        variantObserver.observe(container, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        });
      }
    });
  }

  observeVariantContainers();

  const bodyObserver = new MutationObserver(function() {
    observeVariantContainers();
    initCustomSelects();
  });

  if (document.body) {
    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

})();
